import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { usePermissions, useTranslation } from '@michelin/central-provider';
import { Grid } from '@material-ui/core';
import {
  Panel, PreventTransitionPrompt, Select, Switch, TextField,
} from '@michelin/acid-components';
import TextFieldUOM, { UOM } from '@michelin/acid-components/build/components/TextFieldUOM/TextFieldUOM';
import { GenericActionButtonsMassUpdate } from 'components/GenericActionButtons/GenericActionButtonsMassUpdate';
import useUtils from 'hooks/useUtils';
import HoursOfOperations from 'components/HoursOfOperation/Index';
import { CommercialLocation_MassST } from 'components/Commercial/Fields';
import { getOptions } from '@michelin/select-options-provider';
import usePermissionsSPP from 'hooks/usePermissionsSPP';
import POSInfoIcon from 'components/POS/POSInfoIcon';
import { checkCommercialMaxServiceRadius, checkCommercialMinServiceRadius } from 'components/Location/LocationPanel';
import { MustEnableField } from '../MustEnableField';
import { HoursOfOperation as HoursOfOperationType } from '../../../Types';

interface Props {
  action: 'edit' | 'view'
  fromMassUpdate?: boolean,
}

function LocationInformation(props: Props) {
  const { t } = useTranslation();
  const { urlRegex } = useUtils();
  const readOnly = props.action === 'view';
  const { location } = usePermissions();
  const permissions = usePermissionsSPP();
  const country: string = location?.customer_country.toString().toLocaleLowerCase() || 'us';
  const {
    formState, reset, control, getValues, setValue, handleSubmit, watch,
  } = useForm<CommercialLocation_MassST>({ defaultValues: {} as CommercialLocation_MassST });

  const copyHoo = () => {
    const newHoo = { ...getValues().tire_services_hours_of_operation };
    setValue('mechanical_hours_of_operation', newHoo || {} as HoursOfOperationType, { shouldDirty: true });
  };
  const copyHooFromTire = () => {
    setValue('mechanical_hours_of_operation', {} as HoursOfOperationType);
    copyHoo();
  };
  const renderMichelinIntegrations = () => (
    <Panel title={t('Michelin Integrations')}>
      <Grid container spacing={2}>
        <POSInfoIcon />
        <Controller
          name="pos_type"
          control={control}
          render={(item) => (
            <MustEnableField
              defaultValue=""
              onChange={item.field.onChange}
              value={item.field.value}
              label={t('Update POS Integration')}
              input={(
                <Select
                  id={item.field.name}
                  name="pos_type"
                  data-testid="pos_type-test"
                  label={t('POS Integration')}
                  disabled={item.field.value === undefined}
                  value={item.field.value}
                  style={{ width: '100%' }}
                  readOnly={readOnly}
                  options={getOptions('pos_type')}
                  onChange={(e) => item.field.onChange(e.target.value)}
                />
                )}
            />
          )}
        />
      </Grid>
    </Panel>
  );
  const renderHoursOfOperations = () => {
    const sameAsTire = watch('mechanical_same_hours_as_tires') || false;
    return (
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={6}>
          <Panel title={t('Tire Services Hours of Operations')}>
            <Grid item xs={12} md={12} style={{ paddingTop: '38px' }}>
              <Controller
                name="tire_services_hours_of_operation"
                control={control}
                render={(item) => (
                  <HoursOfOperations
                    onChange={(newHoo: HoursOfOperationType) => {
                      item.field.onChange(newHoo);
                      if (sameAsTire) copyHoo();
                    }}
                    hoursOfOperation={item.field.value || {} as HoursOfOperationType}
                    readOnly={readOnly}
                    country={country}
                    differentialUpdate
                  />
                )}
              />
            </Grid>
          </Panel>
        </Grid>
        <Grid item xs={12} md={6}>
          <Panel title={t('Mechanical Services Hours of Operations')}>
            <Controller
              name="mechanical_same_hours_as_tires"
              control={control}
              render={(item) => (
                <Switch
                  id={item.field.name}
                  labelPosition="left"
                  label={t('Mechanical same as Tires')}
                  readOnly={readOnly}
                  color="primary"
                  uncheckedLabel={t('No')}
                  checkedLabel={t('Yes')}
                  checked={item.field.value === true}
                  onChange={(event, newValue) => {
                    if (newValue) copyHoo();
                    else { setValue('mechanical_hours_of_operation', {} as HoursOfOperationType, { shouldDirty: true }); }
                    item.field.onChange(newValue);
                  }}
                />
              )}
            />
            <Controller
              name="mechanical_hours_of_operation"
              control={control}
              render={(item) => (
                <HoursOfOperations
                  onChange={(newHoo: HoursOfOperationType) => {
                    item.field.onChange(newHoo);
                  }}
                  hoursOfOperation={item.field.value || {} as HoursOfOperationType}
                  copyWorkHoursFrom={copyHooFromTire}
                  readOnly={readOnly}
                  country={country}
                  differentialUpdate
                  copyMode={sameAsTire}
                />
              )}
            />
          </Panel>
        </Grid>
      </Grid>
    );
  };
  return (
    <>
      {(props.fromMassUpdate || false) && (
        <GenericActionButtonsMassUpdate
          isDirty={formState.isDirty}
          getValues={getValues}
          reset={reset}
          handleSubmit={handleSubmit}
          validateAction="mass_st_update.update"
          hideAdd
          hideRemove
        />
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={12}>
          <Panel title={t('General Information')}>
            <Grid container spacing={2}>
              <Controller
                name="commercial_information.open_for_commercial_business"
                control={control}
                render={(item) => (
                  <MustEnableField
                    defaultValue={false}
                    onChange={item.field.onChange}
                    value={item.field.value}
                    label={t('Update Open for Business')}
                    input={(
                      <Switch
                        id={item.field.name}
                        label={t('Open for Business')}
                        disabled={item.field.value === undefined}
                        labelPosition="left"
                        readOnly={readOnly}
                        checked={item.field.value === true}
                        color="primary"
                        uncheckedLabel={t('No')}
                        checkedLabel={t('Yes')}
                        onChange={(event, newValue) => item.field.onChange(newValue)}
                      />
                    )}
                  />
                )}
              />
              <Controller
                name="is_mobile_only"
                control={control}
                render={(item) => (
                  <MustEnableField
                    defaultValue={false}
                    onChange={item.field.onChange}
                    value={item.field.value}
                    label={t('Update Mobile Only')}
                    input={(
                      <Switch
                        id={item.field.name}
                        label={t('Mobile Only')}
                        disabled={item.field.value === undefined}
                        labelPosition="left"
                        readOnly={readOnly}
                        checked={item.field.value === true}
                        color="primary"
                        uncheckedLabel={t('No')}
                        checkedLabel={t('Yes')}
                        onChange={(event, newValue) => item.field.onChange(newValue)}
                      />
                    )}
                  />
                )}
              />
              <Controller
                name="commercial_information.service_radius"
                control={control}
                render={(item) => (
                  <MustEnableField
                    defaultValue=""
                    onChange={item.field.onChange}
                    value={item.field.value}
                    label={t('Update Service Radius')}
                    inputContainerStyle={{ paddingTop: 0 }}
                    input={(
                      <TextFieldUOM
                        id="service_radius"
                        label={t('Service Radius')}
                        disabled={item.field.value === undefined}
                        variant="standard"
                        style={{ display: 'flex', flexWrap: 'wrap' }}
                        uom={country === 'us' ? UOM.miles : UOM.km}
                        onChange={(newValue: string) => ((newValue === '')
                          ? item.field.onChange('') : item.field.onChange(checkCommercialMaxServiceRadius(newValue, country)))}
                        onBlur={() => item.field.onChange(checkCommercialMinServiceRadius(item.field.value, country))}
                        value={checkCommercialMaxServiceRadius(item.field.value?.toString(), country) || undefined}
                        decimals={2}
                        min={0}
                        step={1}
                        readOnly={readOnly}
                      />
                    )}
                  />
                )}
              />
              <Controller
                name="commercial_information.site_url"
                control={control}
                rules={{ pattern: urlRegex }}
                render={(item) => (
                  <MustEnableField
                    defaultValue=""
                    onChange={item.field.onChange}
                    value={item.field.value}
                    label={t('Update Website Address')}
                    inputContainerStyle={{ paddingTop: 0 }}
                    input={(
                      <TextField
                        label={t('Website Address')}
                        disabled={item.field.value === undefined}
                        value={item.field.value || ''}
                        variant="standard"
                        readOnly={readOnly}
                        onChange={(e) => item.field.onChange(e.target.value)}
                        error={!!item.formState.errors.commercial_information?.site_url}
                      />
                    )}
                  />
                )}
              />
            </Grid>
          </Panel>
        </Grid>
        {permissions.allowsAction('michelin_integrations.update_pos_integration')
          && (
          <Grid item xs={12} md={12}>
            {renderMichelinIntegrations()}
          </Grid>
          )}
        <Grid item xs={12} md={12}>
          {renderHoursOfOperations()}
        </Grid>
      </Grid>
      <PreventTransitionPrompt
        when={formState.isDirty}
      />
    </>
  );
}

export default LocationInformation;
